import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import numeral from 'numeral';
import { BarLoader } from 'react-spinners';
import { isEqual } from 'lodash';
import { motion, AnimatePresence, useAnimation } from "framer-motion";

import styled from 'styled-components';

import DropDownSelect from 'SharedComponents/dropdown-select';
import PopoverHover from 'SharedComponents/popover-hover';
import ActionButton from 'SharedComponents/action-button';
import PromoCodeActivation from 'SharedComponents/promo-code-activation';
import FeatureIcons from 'SharedComponents/feature-icons';

import log from "Utils/logger";

import * as SubscriptionActions from 'Flux/subscriptions/actions';
import * as RoundUpsActions from 'Flux/round-ups/actions';

import useFetchSubscriptions from 'SharedHooks/fetch-subscriptions';

import styles from './index.css';

// Define default format for numbers in numeral
numeral.defaultFormat('0,0.00');

// Set default format for nulls
numeral.nullFormat('0.00');
numeral.zeroFormat('0.00');


const SelectSubscriptionPlanWrapper = styled.div`${styles}`;

const SelectSubscriptionPlan = ({ subscriptionFeaturesOn, sponsorFilter }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const roundupsPausedStatus = useSelector(state => state.roundups.roundUpsPaused);
  const authentication = useSelector(state => state.authentication);
  
  const [searchParams] = useSearchParams();
  const [isEmployerPage, setIsEmployerPage] = useState(true);
  const [isHomePage, setIsHomePage] = useState(true);
  const [isAccountPage, setIsAccountPage] = useState(false);
  
  const previousActivePlanIDRef = useRef(null);
  const [activePlanJustChanged, setActivePlanJustChanged] = useState(false);

  const [updatingRoundupsPauseStatus, setUpdatingRoundupsPauseStatus] = useState(false);

  const { fetchSubscriptions } = useFetchSubscriptions();


  useEffect(() => {
    console.log('SelectSubscriptionPlan - useEffect triggered with location.path :', location.pathname);
    const pathname = location.pathname;
    const tab = searchParams.get("tab")?.toLowerCase() || null;
    console.log('SelectSubscriptionPlan - useEffect triggered with tab:', tab);
    if (pathname === "/partnerships" && !tab) {
      console.log("SelectSubscriptionPlan - useEffect triggered 🛑 tab param not yet available — skipping update");
      return;
    }
    console.log('SelectSubscriptionPlan - useEffect triggered with pathname:', pathname);
  
    const nextIsEmployerPage = pathname === "/partnerships" && tab === "employers";
    const nextIsHomePage = pathname === "/";
    const nextIsAccountPage = pathname === "/account" && tab === "current-plan";
  
    //if (isEmployerPage !== nextIsEmployerPage) {
      console.log('SelectSubscriptionPlan - useEffect triggered ** isEmployerPage is:', isEmployerPage);
      console.log('SelectSubscriptionPlan - useEffect triggered ** nextIsEmployerPage is:', nextIsEmployerPage);
      console.log('SelectSubscriptionPlan - useEffect triggered ** updating isEmployerPage to:', nextIsEmployerPage);
      setIsEmployerPage(nextIsEmployerPage);
    //}
  
    //if (isHomePage !== nextIsHomePage) {
      console.log('SelectSubscriptionPlan - useEffect triggered with isHomePage: ', isHomePage);
      console.log('SelectSubscriptionPlan - useEffect triggered with nextIsHomePage: ', nextIsHomePage);
      console.log('SelectSubscriptionPlan - useEffect triggered ** updating isHomePage to:', nextIsHomePage);

      setIsHomePage(nextIsHomePage);
    //}

    //if (isAccountPage !== nextIsAccountPage) {
      console.log('SelectSubscriptionPlan - useEffect triggered with isAccountPage: ', isAccountPage);
      console.log('SelectSubscriptionPlan - useEffect triggered with nextIsAccountPage: ', nextIsAccountPage);
      console.log('SelectSubscriptionPlan - useEffect triggered ** updating isAccountPage to:', nextIsAccountPage);

      setIsAccountPage(nextIsAccountPage);
  }, [location.pathname, searchParams.toString()]
);
  
  const roundupLinkedAccounts = useSelector((state) => state.roundups.linkedAccounts);

  const isRoundUpsSetup = useMemo(() => {
    return roundupLinkedAccounts && roundupLinkedAccounts.length > 0;
  }, [roundupLinkedAccounts]);

  const availableSubscriptionPlans = useSelector(state => state.subscriptions.availSubscriptionPlans);

  const shopperSubscribedPlan = useSelector(
    state => state.subscriptions.availSubscriptionPlans.find(
      plan => plan.shopperSubscribed === true
    )
  )

  const localAvailableSubscriptionPlans = useMemo(() => {
    return sponsorFilter
      ? availableSubscriptionPlans.filter(plan => plan.subscriptionSponsor === sponsorFilter)
      : availableSubscriptionPlans;
  }, [availableSubscriptionPlans, sponsorFilter]);

  const [selectedPlanID, setSelectedPlanID] = useState(null);

  const selectedSubscriptionPlan = useMemo(() => {
    return localAvailableSubscriptionPlans.find(
      plan => plan.subscriptionID === selectedPlanID
    ) || null;
  }, [localAvailableSubscriptionPlans, selectedPlanID]);

  const isEmployerPlan =
    selectedSubscriptionPlan?.subscriptionName.toLowerCase().includes("employer");

  const activePlan = useSelector(state =>
    state.subscriptions.availSubscriptionPlans.find(plan => plan.shopperSubscribed)
  );
  
  const isActivePlanSelected = () => activePlan?.subscriptionID === selectedSubscriptionPlan?.subscriptionID;
  


  useEffect(() => {
    if (!localAvailableSubscriptionPlans.length) return;

    const defaultPlan = localAvailableSubscriptionPlans.find(plan => plan.subscriptionOptionSelected) 
      || localAvailableSubscriptionPlans.find(plan => plan.shopperSubscribed)
      || localAvailableSubscriptionPlans.find(plan => plan.subscriptionAmount > 0);

    
  
    if (defaultPlan) {
      console.log('selectedSubscriptionPlan useEffect localAvailableSubscriptionPlans triggered and defaultPlan found: ', defaultPlan);
      console.log('selectedSubscriptionPlan useEffect localAvailableSubscriptionPlans triggered and setting selectedPlanID to: ', defaultPlan.subscriptionID);
      setSelectedPlanID(defaultPlan.subscriptionID);
    }
  }, [localAvailableSubscriptionPlans]);
  
    
  useEffect(() => {

    console.log('selectedSubscriptionPlan useEffect : selectedPlanID changed to: ', selectedPlanID);

    if (!selectedPlanID ) return;
  
    const newSelectedSubscriptionPlans = availableSubscriptionPlans.map(plan => ({
      ...plan,
      subscriptionOptionSelected: plan.subscriptionID === selectedPlanID, // ✅ Correctly sets true/false
    }));
  
    console.log('selectedSubscriptionPlan useEffect Updated Subscription Plans newSelectedSubscriptionPlans:', newSelectedSubscriptionPlans);
    console.log('selectedSubscriptionPlan useEffect Available Subscription Plans availableSubscriptionPlans:', availableSubscriptionPlans);
    console.log('selectedSubscriptionPlan useEffect selectedPlanID triggered by user selecting a plan with selectedPlanID: ', selectedPlanID);

    if (!isEqual(newSelectedSubscriptionPlans, availableSubscriptionPlans)) {
      console.log('selectedSubscriptionPlan useEffect - dispatching setSelectedSubscriptionPlan with newSelectedSubscriptionPlans: ', newSelectedSubscriptionPlans);
      dispatch(SubscriptionActions.setSelectedSubscriptionPlan(newSelectedSubscriptionPlans));
    }
  
  }, [selectedPlanID]);

  useEffect(() => {
    const currentActivePlanID = shopperSubscribedPlan?.subscriptionID;

    // The subscription data has not loaded yet.
    if (!currentActivePlanID) return;

    // Establish the initial active plan without animating on page load.
    if (previousActivePlanIDRef.current === null) {
      previousActivePlanIDRef.current = currentActivePlanID;
      return;
    }

    // Only animate when the actual activated plan changes.
    if (previousActivePlanIDRef.current !== currentActivePlanID) {
      previousActivePlanIDRef.current = currentActivePlanID;
      setActivePlanJustChanged(true);

      const animationTimer = setTimeout(() => {
        setActivePlanJustChanged(false);
      }, 800);

      return () => clearTimeout(animationTimer);
    }
  }, [shopperSubscribedPlan?.subscriptionID]);

  useEffect(() => {

    if (!activePlan) return;

    // Wait until the Round-Ups paused status has loaded
    if (
      roundupsPausedStatus === undefined ||
      roundupsPausedStatus === null
    ) {
      return;
    }

    const shouldPauseRoundups =
      Number(activePlan.subscriptionAmount) === 0;

    console.log(
      'activePlan Round-Ups check:',
      {
        subscriptionID: activePlan.subscriptionID,
        subscriptionName: activePlan.subscriptionName,
        subscriptionAmount: activePlan.subscriptionAmount,
        roundupsPausedStatus,
        shouldPauseRoundups,
      }
    );

    if (roundupsPausedStatus !== shouldPauseRoundups) {
      console.log(
        'activePlan Round-Ups status needs changing to:',
        shouldPauseRoundups
      );

      setRoundupsPause(shouldPauseRoundups);
    }

  }, [
    activePlan?.subscriptionID,
    activePlan?.subscriptionAmount,
    roundupsPausedStatus
  ]);
  

  console.log('SelectSubscriptionPlan triggered with localAvailableSubscriptionPlans: ', localAvailableSubscriptionPlans);

  const formattedOptions = useMemo(() => {
    return localAvailableSubscriptionPlans.map(plan => ({
      value: plan.subscriptionID,
      label: (
        <div className="selection-bar"> {/* style={{ display: 'flex', flexDirection: 'column' }}> */}
          <div className="selection-and-amount">
            <span 
              className="selection-name"
              style={{ 
                color: plan.subscriptionName?.includes('Fastest') ? '#ffba00' 
                      : plan.subscriptionName?.includes('Faster') ? '#20b2aa'
                      : '#000', 
              }}>{plan.subscriptionName}</span>
            {/* <span style={{ fontSize: '14px', color: 'gray' }}>${plan.subscriptionAmount.toFixed(2)} {plan.subscriptionPeriod}</span> */}
            <span className="selection-amount">${plan.subscriptionAmount.toFixed(2)} {plan.subscriptionPeriod}</span>
          </div>
          <div className="selection-activate-plan">
            {shopperSubscribedPlan?.subscriptionID === plan.subscriptionID ? (
              activePlanJustChanged ? (
                <motion.span
                  className="active-plan"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{
                    scale: [0.6, 1.25, 0.95, 1],
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeOut",
                  }}
                >
                  ACTIVE PLAN
                </motion.span>
              ) : (
                <span className="active-plan">
                  ACTIVE PLAN
                </span>
              )
            ) : (
              plan.subscriptionAvailable ? null : "Available Soon"
            )}
          </div>
        </div>
      ),
    }));
  }, [localAvailableSubscriptionPlans, shopperSubscribedPlan, activePlanJustChanged]);
  
  console.log('SelectSubscriptionPlan triggered with formattedOptions: ', formattedOptions);
  console.log('SelectSubscriptionPlan triggered with selectedSubscriptionPlan: ', selectedSubscriptionPlan);
  console.log('SelectSubscriptionPlan triggered with selectedPlanID: ', selectedPlanID);

  const [updatingSubscriptionPlan, setUpdatingSubscriptionPlan] = useState(false);

  
  useEffect(() => {

    console.log('selectSubscriptionPlan - mounted');
    return (() => {
      console.log('selectSubscriptionPlan - unmounted');
    });
  }, []);

  
  
  const onChangeSubscriptionPlan = (value) => {
    console.log('onChangeDefaultSubscriptionPlan triggered');
    console.log('onChangeDefaultSubscriptionPlan new value is: ', value);
    setSelectedPlanID(value.value);
  };


  async function setRoundupsPause(shouldPause) {
    console.log(
      'setRoundupsPause triggered with shouldPause:',
      shouldPause,
      'current roundupsPausedStatus:',
      roundupsPausedStatus
    );

      // Nothing to do if it is already in the correct state
    if (roundupsPausedStatus === shouldPause) {
      console.log('setRoundupsPause - already correct, skipping');
      return;
    }

    try {
      const result = await dispatch(RoundUpsActions.updateRoundUpsPauseStatus(shouldPause));

      console.log('setRoundupsPause result:', result);
      dispatch(RoundUpsActions.setRoundUpPauseStatus(result.data.data.roundUpsPaused));
    } catch (exception) {
    console.log('setRoundupsPause exception: ', exception);
    log({
      event: "System-Error",
      'shopper_id': authentication? authentication.user.shopper_id : 'Not Signed In',
      source: "SelectSubscriptionPlan - SetRoundupsPause",
      info: exception,
      ...exception,
    });
    } finally {
      //console.log('setRoundupsPause finalised with roundupsPausedStatus: ', roundupsPausedStatus);
      setUpdatingRoundupsPauseStatus(false);
    }
  }

  return (
    <SelectSubscriptionPlanWrapper>

        <div className="subscription-container">
                      {/*  isRoundUpsSetup: {isRoundUpsSetup ? "Yes" : "No"} <br/>
                      isAccountPage: {isAccountPage ? "Yes" : "No"} <br/>
                      isEmployerPlan: {isEmployerPlan ? "Yes" : "No"} <br/>
                      isActivePlanSelected: {isActivePlanSelected() ? "Yes" : "No"} <br/>
                      activePlan: {activePlan ? activePlan.subscriptionName : "None"} <br/> */}
          <div className="item">
            <div className="title-and-limit">
              <h2 className="title">Select A Plan</h2>
              <div className="limit">
              { updatingSubscriptionPlan
                  ? <div className="loading-container">
                    <span>Updating...</span>
                    <BarLoader
                      sizeUnit={"px"}
                      size={400}
                      color="#000"
                      loading={true}
                    />
                    </div>
                  : 
                    <DropDownSelect
                      className="dropdown-menu"
                      value={selectedPlanID}
                      onChange={onChangeSubscriptionPlan}
                      options={formattedOptions}
                      dropUp={true}
                      style={{ width: '100%' , backgroundColor: 'white' }}
                    />
              }
              </div>

                {/* roundupLinkedAccounts && roundupLinkedAccounts.length > 0 */}

              <AnimatePresence mode="wait">
                {
                  isEmployerPage  || isHomePage 
                  ? null
                  : (
                    <div className="plan-activation">

                      { 
                      
                       !isRoundUpsSetup && isAccountPage && isActivePlanSelected() && activePlan?.subscriptionAmount > 0
                          ? (
                            <motion.div
                              key="promo-details"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: "easeInOut" }}
                              style={{ overflow: "hidden" }}
                            >
                              <span className="note-and-link" style={{color: "red" }}>
                                You now need to set up your Round-Ups account &nbsp;
                                <span
                                  className="clickable-link"
                                  onClick={() => navigate("/settings?tab=round-ups")}
                                >
                                  here
                                </span>
                                .
                              </span>
                            </motion.div>
                          )
                      
                      : isEmployerPlan
                        ? (
                            <motion.div
                              key="promo-details"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: "easeInOut" }}
                              style={{ overflow: "hidden" }}
                            >
                              <span className="note-and-link">
                                This plan requires an employer-provided code. &nbsp;
                                <span
                                  className="clickable-link"
                                  onClick={() => navigate("/partnerships?tab=employers")}
                                >
                                  More
                                </span>
                                .
                              </span>
                            </motion.div>
                          )
                          
                          
                          :null
                      }
                      <PromoCodeActivation/>
                    </div>
                  )}
             
              </AnimatePresence>

            </div>

            { subscriptionFeaturesOn && (
              <div className="value-prop-and-features">
                <div className="value-prop-container">
                  <AnimatePresence mode="wait">
                    {selectedSubscriptionPlan?.subscriptionValueProp && (
                      <motion.div
                        key={selectedSubscriptionPlan.subscriptionValueProp} // 🔹 Triggers animation on change
                        className="value-prop"
                        initial={{ scale: 1, opacity: 1 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }} // 🔹 Shrinks before disappearing
                        transition={{ duration: 0.3 }} // 🔹 Smooth transition
                      >
                        {selectedSubscriptionPlan.subscriptionValueProp}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>


                <FeatureIcons
                  selectedSubscriptionPlan={selectedSubscriptionPlan}
                />
              </div>
            )}
          </div>
          {/* </MotionFadeWrapper> */}
        </div>

    </SelectSubscriptionPlanWrapper>
  );
}

export default SelectSubscriptionPlan;




